export const QUEUE_STATUS = {
  PENDING: 'pending',
  RESOLVED: 'resolved',
};

export const QUEUE_VIEW = {
  PENDING: 'pending',
  HISTORY: 'history',
};

export const QUEUE_VIEW_OPTIONS = [
  { key: QUEUE_VIEW.PENDING, label: 'Pending Review' },
  { key: QUEUE_VIEW.HISTORY, label: 'Review History' },
];

export const LMS_CODE = 'canvas';

/** Only suggest/link resources with this tag (courses). */
export const SUGGESTED_RESOURCE_TAG = 'LEARNING_OBJECT_COURSE';

/**
 * Single source of truth for review statuses.
 * Each status has a key (API value) and label (UI display).
 */
export const REVIEW_STATUS = {
  PENDING: { key: 'PENDING', label: 'Pending' },
  LINKED_TO_EXISTING: { key: 'LINKED_TO_EXISTING', label: 'Linked to Existing' },
  ADDED_AS_NEW: { key: 'ADDED_AS_NEW', label: 'Added as New' },
};

/**
 * Get the display label for a review status key.
 * @param {string} key - The API review status key
 * @returns {string} The display label or the key if not found
 */
export const getReviewStatusLabel = (key) =>
  Object.values(REVIEW_STATUS).find((s) => s.key === key)?.label ?? key;

/**
 * Get the API key for a review status label.
 * @param {string} label - The display label
 * @returns {string|undefined} The API key or undefined if not found
 */
export const getReviewStatusKey = (label) =>
  Object.values(REVIEW_STATUS).find((s) => s.label === label)?.key;

/**
 * Check if a review status represents a resolved state.
 * @param {string} key - The API review status key
 * @returns {boolean}
 */
export const isResolvedStatus = (key) =>
  key === REVIEW_STATUS.LINKED_TO_EXISTING.key || key === REVIEW_STATUS.ADDED_AS_NEW.key;

/** DataGrid MultiTagCell filter labels */
export const STATUS_FILTER_OPTIONS = Object.values(REVIEW_STATUS).map((s) => s.label);

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
export const SEARCH_DEBOUNCE_MS = 300;
export const SEARCH_MIN_LENGTH = 2;
export const SEARCH_DEFAULT_LIMIT = 10;

/** Toast suffix after a successful Course Review link (Approach A enqueue is best-effort on API). */
export const COMPLETION_RULES_QUEUED_TOAST =
  'Completion rules migration was queued for this Canvas course.';

/** Selection checkbox column (parent-controlled; keep `id` as first column for DataGrid row keys). */
export const QUEUE_SELECT_COLUMN = {
  name: 'select',
  displayName: '',
  sortOrder: 'none',
  isFilterable: false,
  isSortable: false,
  isVisible: true,
  type: 'override',
};

export const QUEUE_COLUMNS = [
  {
    name: 'id',
    displayName: 'Catalog ID',
    sortOrder: 'none',
    isFilterable: false,
    isSortable: false,
    isVisible: false,
    type: 'value',
  },
  QUEUE_SELECT_COLUMN,
  {
    name: 'canvasCourseName',
    displayName: 'Course Name',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: false,
    isVisible: true,
    type: 'override',
  },
  {
    name: 'canvasCourseId',
    displayName: 'Canvas ID',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: false,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'queuedAt',
    displayName: 'Date Added to Queue',
    sortOrder: 'none',
    isFilterable: false,
    isSortable: false,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'status',
    displayName: 'Status',
    sortOrder: 'none',
    isFilterable: true,
    isSortable: false,
    isVisible: true,
    type: 'override',
    filter: {
      type: 'MultiTagCell',
      showBlankFilter: false,
    },
    options: STATUS_FILTER_OPTIONS,
  },
  {
    name: 'suggestedMatch',
    displayName: 'Suggested Match',
    sortOrder: 'none',
    isFilterable: false,
    isSortable: false,
    isVisible: true,
    type: 'override',
  },
  {
    name: 'linkedResourceId',
    displayName: 'Orijin Course ID',
    sortOrder: 'none',
    isFilterable: false,
    isSortable: false,
    isVisible: true,
    type: 'value',
  },
  {
    name: 'migrationStatus',
    displayName: 'Completion Rules',
    sortOrder: 'none',
    isFilterable: false,
    isSortable: false,
    isVisible: true,
    type: 'override',
  },
  {
    name: 'actions',
    displayName: 'Action',
    sortOrder: 'none',
    isFilterable: false,
    isSortable: false,
    isVisible: true,
    type: 'override',
  },
];

/** Columns for the queue grid; view-specific: pending omits linked ID, history omits selection. */
export const getQueueColumns = (isHistoryView = false) =>
  QUEUE_COLUMNS.filter((column) => {
    if (isHistoryView && column.name === 'select') return false;
    if (!isHistoryView && column.name === 'linkedResourceId') return false;
    if (!isHistoryView && column.name === 'migrationStatus') return false;
    return true;
  });
